/**
 * IDoScreen - K Addition Within 5 I Do Practice
 *
 * Shows the addition equation and demonstrates the answer.
 * Displays "X + Y = ?" then reveals the sum with TTS.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { useKTTS } from '../../../hooks/useKAudio';
import { TRACK_RANGES } from '../../../types/constants';
import { ADDITION_FACTS } from './types';

import {
  PracticeHeader,
  PRACTICE_PHASES,
} from '../../PreKPractice/shared';

const K_TRACK_ID = 'TRACK_K_ADDITION_WITHIN_5';

/**
 * Convert K addition fact ID to index into ADDITION_FACTS array
 * FACT1685 = index 0, FACT1686 = index 1, ..., FACT1696 = index 11
 */
const factIdToIndex = (factId: string): number => {
  const factNumber = parseInt(factId.replace('FACT', ''));
  const [startFact] = TRACK_RANGES.TRACK_K_ADDITION_WITHIN_5;
  return factNumber - startFact;
};

interface IDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
}

const IDoScreen: React.FC<IDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<'showing' | 'equation' | 'result'>('showing');
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const { playTTSWithCallback, isTTSPlaying } = useKTTS();

  const currentFactId = factsToPractice[currentFactIndex];
  const factArrayIndex = currentFactId ? factIdToIndex(currentFactId) : undefined;
  const currentFact = factArrayIndex !== undefined ? ADDITION_FACTS[factArrayIndex] : undefined;

  // Reset phase when fact changes
  useEffect(() => {
    if (currentFact === undefined) return;

    // Clear any pending timeouts from previous fact
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    setCurrentPhase('showing');
  }, [currentFact, currentFactIndex]);

  // Auto-advance to next fact after showing sequence
  const startAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
    }

    const timer = setTimeout(() => {
      if (currentFactIndex < factsToPractice.length - 1) {
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // All facts completed, move to next screen
        onComplete();
      }
    }, 1000);

    setAutoAdvanceTimer(timer);
  }, [currentFactIndex, factsToPractice.length, onComplete, autoAdvanceTimer]);

  // Start the demonstration sequence when in showing phase
  useEffect(() => {
    if (currentPhase === 'showing' && currentFact !== undefined) {
      // Small delay then start equation phase
      const timeout1 = setTimeout(() => {
        setCurrentPhase('equation');

        // Show equation briefly, then show result with TTS
        const timeout2 = setTimeout(() => {
          setCurrentPhase('result');

          if (settings.speechEnabled) {
            // Play "X plus Y equals Z" instruction
            const audioFile = {
              filename: `kPractice/additionWithin5/k_addition_ido_${currentFact.operand1}_${currentFact.operand2}.mp3`,
              text: `${currentFact.operand1} plus ${currentFact.operand2} equals ${currentFact.sum}.`,
            };

            playTTSWithCallback(audioFile, () => {
              startAutoAdvance();
            }).catch(() => {
              console.error('IDoScreen addition TTS failed for fact:', currentFact);
              startAutoAdvance();
            });
          } else {
            startAutoAdvance();
          }
        }, 800);
        timeoutsRef.current.push(timeout2);
      }, 500);
      timeoutsRef.current.push(timeout1);
    }
  }, [currentPhase, currentFact, settings.speechEnabled, playTTSWithCallback, startAutoAdvance]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [autoAdvanceTimer]);

  // Loading state
  if (currentFact === undefined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.I_DO}
        onBack={onBack}
        progress={
          factsToPractice.length > 0
            ? Math.min((currentFactIndex + 1) / factsToPractice.length, 1)
            : 0
        }
      />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-6 gap-12">
        {/* Showing Phase - Get Ready */}
        {currentPhase === 'showing' && (
          <div className="text-center">
            <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
              Get ready...
            </div>
            <div className="text-lg text-gray-500">Watch carefully!</div>
          </div>
        )}

        {/* Equation Phase - Show equation with ? */}
        {currentPhase === 'equation' && (
          <div className="flex flex-col items-center gap-8">
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="text-6xl lg:text-7xl font-bold text-gray-800"
              style={NUMERAL_FONT_STYLE}
            >
              {currentFact.operand1} + {currentFact.operand2} = ?
            </motion.div>

            <div className="text-xl text-gray-600 animate-pulse">
              What is the answer?
            </div>
          </div>
        )}

        {/* Result Phase - Show equation with answer */}
        {currentPhase === 'result' && (
          <div className="flex flex-col items-center gap-8">
            <motion.div
              initial={{ scale: 1 }}
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 0.5 }}
              className="text-6xl lg:text-7xl font-bold text-gray-800"
              style={NUMERAL_FONT_STYLE}
            >
              {currentFact.operand1} + {currentFact.operand2} = <span className="text-green-600">{currentFact.sum}</span>
            </motion.div>

            {/* Large Answer Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="w-48 h-48 lg:w-56 lg:h-56 bg-gradient-to-br from-green-400 to-green-600 rounded-3xl flex items-center justify-center shadow-2xl border-4 border-green-700"
              style={NUMERAL_FONT_STYLE}
            >
              <span className="text-8xl lg:text-9xl font-bold text-white">{currentFact.sum}</span>
            </motion.div>
          </div>
        )}
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default IDoScreen;
